import { useState, useEffect, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Search, Filter, X, ZoomIn, ChevronLeft, ChevronRight, Info, Package, Shield, Wrench, Share2, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Banner, BannerGrid } from '../components/Banner';
import { categories as defaultCategories, products as mockProducts, brands as mockBrands } from '../data/mock';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const WHATSAPP_NUMBER = '5594991858158';

// Usar URL atual do site (dinâmica)
const getSiteUrl = () => {
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  return process.env.REACT_APP_BACKEND_URL || 'https://casadasferramentas.com.br';
};

// Função para gerar slug do produto
const generateSlug = (product) => {
  if (!product) return '';
  let slug = product.name.toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[áàã]/g, 'a')
    .replace(/[éè]/g, 'e')
    .replace(/[íì]/g, 'i')
    .replace(/[óòõ]/g, 'o')
    .replace(/[úù]/g, 'u')
    .replace(/ç/g, 'c')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
  return `${slug}-${product.id?.slice(0, 8) || ''}`;
};

// Ícone SVG do WhatsApp
const WhatsAppIcon = ({ className = "w-5 h-5" }) => (
  <svg viewBox="0 0 24 24" className={`${className} fill-current`}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

// Modal de Detalhes do Produto
const ProductModal = ({ product, isOpen, onClose, onWhatsApp, brands, categories }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isZoomed, setIsZoomed] = useState(false);
  const [copied, setCopied] = useState(false);

  if (!isOpen || !product) return null;

  const images = product.images?.length > 0 ? product.images : [product.image || 'https://placehold.co/600x600?text=Produto'];

  const nextImage = () => setCurrentImageIndex((prev) => (prev + 1) % images.length);
  const prevImage = () => setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);

  // Encontrar logo da marca (case-insensitive)
  const brandData = brands?.find(b => b.name.toLowerCase() === product.brand.toLowerCase());
  const brandLogo = brandData?.logo;

  // Gerar URL do produto para compartilhamento
  const productSlug = generateSlug(product);
  const productUrl = `${getSiteUrl()}/produto/${productSlug}`;

  // Função para compartilhar produto - apenas copia URL
  const handleShare = async () => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      try {
        await navigator.clipboard.writeText(productUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (e) {
        window.prompt('Copie o link:', productUrl);
      }
    } else {
      window.prompt('Copie o link:', productUrl);
    }
  };

  // Encontrar nome da categoria
  const getCategoryName = () => {
    const cat = categories?.find(c => 
      c.id === product.category || 
      c.slug === product.category ||
      c.name?.toLowerCase() === product.category?.toLowerCase()
    );
    return cat?.name || product.category;
  };

  // Especificações técnicas
  const getSpecs = () => {
    const baseSpecs = [
      { label: 'Categoria', value: getCategoryName(), icon: Info },
      { label: 'Garantia', value: 'De Fábrica', icon: Shield },
    ];
    
    if (product.specs) {
      return [...baseSpecs, ...product.specs];
    }
    return baseSpecs;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div 
        className="bg-white rounded-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-gray-50">
          <div className="flex items-center gap-4">
            {/* Logo da Marca */}
            {brandLogo ? (
              <div className="bg-white border border-gray-200 rounded-lg p-2 flex items-center justify-center h-12 w-24">
                <img src={brandLogo} alt={product.brand} className="max-h-8 max-w-20 object-contain" />
              </div>
            ) : (
              <div className="bg-blue-900 text-white px-3 py-1 rounded-lg text-sm font-bold">
                {product.brand}
              </div>
            )}
            <h2 className="text-xl md:text-2xl font-bold text-gray-900">{product.name}</h2>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-gray-200 rounded-full transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="grid md:grid-cols-2 gap-0 max-h-[calc(90vh-140px)] overflow-y-auto">
          {/* Imagens */}
          <div className="bg-gray-100 p-4 md:p-6">
            {/* Imagem Principal com Zoom */}
            <div 
              className={`relative aspect-square bg-white rounded-xl overflow-hidden cursor-zoom-in ${isZoomed ? 'cursor-zoom-out' : ''}`}
              onClick={() => setIsZoomed(!isZoomed)}
            >
              <img
                src={images[currentImageIndex]}
                alt={product.name}
                className={`w-full h-full object-contain transition-transform duration-300 ${isZoomed ? 'scale-150' : ''}`}
              />
              <button className="absolute top-3 right-3 p-2 bg-white/90 rounded-full shadow-md hover:bg-white">
                <ZoomIn size={20} className="text-gray-700" />
              </button>
              
              {/* Navegação de Imagens */}
              {images.length > 1 && (
                <>
                  <button 
                    onClick={(e) => { e.stopPropagation(); prevImage(); }}
                    className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-white/90 rounded-full shadow-md hover:bg-white"
                  >
                    <ChevronLeft size={24} />
                  </button>
                  <button 
                    onClick={(e) => { e.stopPropagation(); nextImage(); }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-white/90 rounded-full shadow-md hover:bg-white"
                  >
                    <ChevronRight size={24} />
                  </button>
                </>
              )}
            </div>

            {/* Thumbnails */}
            {images.length > 1 && (
              <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
                {images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentImageIndex(idx)}
                    className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                      idx === currentImageIndex ? 'border-red-600 ring-2 ring-red-200' : 'border-gray-200 hover:border-gray-400'
                    }`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}

            <p className="text-xs text-gray-500 text-center mt-3">
              Clique na imagem para ampliar
            </p>
          </div>

          {/* Informações */}
          <div className="p-4 md:p-6 flex flex-col">
            {/* Descrição */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Descrição</h3>
              <p className="text-gray-600 leading-relaxed">
                {product.description || 'Entre em contato para mais informações sobre este produto.'}
              </p>
            </div>

            {/* Especificações Técnicas */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Especificações</h3>
              <div className="grid grid-cols-2 gap-3">
                {/* Marca com Logo */}
                <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                  <Package size={18} className="text-red-600" />
                  <div>
                    <p className="text-xs text-gray-500">Marca</p>
                    {brandLogo ? (
                      <img src={brandLogo} alt={product.brand} className="h-5 w-auto object-contain mt-1" />
                    ) : (
                      <p className="text-sm font-semibold text-gray-900">{product.brand}</p>
                    )}
                  </div>
                </div>
                {getSpecs().map((spec, idx) => (
                  <div key={idx} className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                    {spec.icon && <spec.icon size={18} className="text-red-600" />}
                    <div>
                      <p className="text-xs text-gray-500">{spec.label}</p>
                      <p className="text-sm font-semibold text-gray-900">{spec.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Aviso */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-amber-800">
                <strong>Atenção:</strong> Preços sujeitos a confirmação. 
                Entre em contato para valores atualizados.
              </p>
            </div>

            {/* Botões de Ação */}
            <div className="mt-auto space-y-3">
              <Button
                className="w-full bg-green-600 hover:bg-green-700 text-white h-12 text-base font-semibold"
                onClick={() => onWhatsApp(product)}
              >
                <WhatsAppIcon className="w-5 h-5 mr-2" />
                Consultar Preço no WhatsApp
              </Button>
              <div className="grid grid-cols-2 gap-3">
                <Button
                  variant="outline"
                  className="border-red-600 text-red-600 hover:bg-red-600 hover:text-white h-12 text-base"
                  onClick={() => window.location.href = '/orcamento?produto=' + product.id}
                >
                  Solicitar Orçamento
                </Button>
                <Button
                  variant="outline"
                  className="border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white h-12 text-base"
                  onClick={handleShare}
                >
                  {copied ? <Check size={18} className="mr-2" /> : <Share2 size={18} className="mr-2" />}
                  {copied ? 'Copiado!' : 'Compartilhar'}
                </Button>
              </div>
              {/* Link para página completa */}
              <Link 
                to={`/produto/${productSlug}`}
                className="block text-center text-sm text-gray-500 hover:text-blue-600 mt-2"
              >
                Ver página completa do produto →
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const Catalogo = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('cat') || '');
  const [selectedBrand, setSelectedBrand] = useState(searchParams.get('marca') || '');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [copiedProductId, setCopiedProductId] = useState(null);
  
  // Dados dinâmicos
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState(defaultCategories);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [productsRes, brandsRes, categoriesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/products`),
        axios.get(`${BACKEND_URL}/api/brands`),
        axios.get(`${BACKEND_URL}/api/categories`)
      ]);
      
      if (productsRes.data && productsRes.data.length > 0) {
        setProducts(productsRes.data);
      } else {
        setProducts(mockProducts);
      }
      
      if (brandsRes.data && brandsRes.data.length > 0) {
        setBrands(brandsRes.data);
      } else {
        setBrands(mockBrands);
      }

      if (categoriesRes.data && categoriesRes.data.length > 0) {
        setCategories(categoriesRes.data);
      }
    } catch (error) {
      setProducts(mockProducts);
      setBrands(mockBrands);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const trackView = async () => {
      try {
        await axios.post(`${BACKEND_URL}/api/track/view?page=catalogo`);
      } catch (e) {}
    };
    trackView();
  }, []);

  const filteredProducts = useMemo(() => {
    return products.filter((product) => {
      const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (product.description || '').toLowerCase().includes(searchTerm.toLowerCase());
      // Comparar categoria por ID, slug ou nome
      const matchesCategory = !selectedCategory || 
        product.category === selectedCategory ||
        categories.find(c => c.id === selectedCategory)?.slug === product.category ||
        categories.find(c => c.id === selectedCategory)?.name?.toLowerCase() === product.category?.toLowerCase();
      const matchesBrand = !selectedBrand || product.brand === selectedBrand;
      return matchesSearch && matchesCategory && matchesBrand;
    });
  }, [products, searchTerm, selectedCategory, selectedBrand, categories]);

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCategory('');
    setSelectedBrand('');
    setSearchParams({});
  };

  const handleWhatsAppClick = async (product) => {
    try {
      await axios.post(`${BACKEND_URL}/api/track/contact?type=whatsapp&product_id=${product.id}&product_name=${encodeURIComponent(product.name)}`);
    } catch (e) {}
    
    const message = encodeURIComponent(`Olá! Tenho interesse no produto: ${product.name}`);
    window.location.href = `https://wa.me/${WHATSAPP_NUMBER}?text=${message}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando produtos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4">
        <Banner position="catalog_featured" className="mb-8" />
        
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Catálogo de Produtos</h1>
          <p className="text-base md:text-lg text-gray-600">
            Explore nossa linha completa de máquinas e ferramentas profissionais
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          <div className="hidden lg:block w-64 flex-shrink-0">
            <BannerGrid position="catalog_side" columns={1} className="sticky top-4" />
          </div>

          <div className="flex-1">
            {/* Search and Filters */}
            <div className="bg-white rounded-xl shadow-md p-4 md:p-6 mb-8">
              <div className="flex flex-col md:flex-row gap-4 mb-4">
                <div className="relative flex-1">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <Input
                    type="text"
                    placeholder="Buscar produtos..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-12 h-12 text-base"
                    data-testid="search-input"
                  />
                </div>
                <Button
                  variant="outline"
                  className="md:hidden"
                  onClick={() => setShowFilters(!showFilters)}
                >
                  <Filter size={20} className="mr-2" />
                  Filtros
                </Button>
              </div>

              <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 ${showFilters ? '' : 'hidden md:grid'}`}>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Categoria</label>
                  <select
                    value={selectedCategory}
                    onChange={(e) => {
                      setSelectedCategory(e.target.value);
                      const params = new URLSearchParams(searchParams);
                      if (e.target.value) params.set('cat', e.target.value);
                      else params.delete('cat');
                      setSearchParams(params);
                    }}
                    className="w-full h-12 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    data-testid="category-filter"
                  >
                    <option value="">Todas as Categorias</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Marca</label>
                  <select
                    value={selectedBrand}
                    onChange={(e) => {
                      setSelectedBrand(e.target.value);
                      const params = new URLSearchParams(searchParams);
                      if (e.target.value) params.set('marca', e.target.value);
                      else params.delete('marca');
                      setSearchParams(params);
                    }}
                    className="w-full h-12 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    data-testid="brand-filter"
                  >
                    <option value="">Todas as Marcas</option>
                    {brands.map((brand) => (
                      <option key={brand.id || brand.name} value={brand.name}>
                        {brand.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {(selectedCategory || selectedBrand || searchTerm) && (
                <div className="flex items-center gap-2 flex-wrap mt-4">
                  <span className="text-sm text-gray-600">Filtros ativos:</span>
                  {selectedCategory && (
                    <Badge variant="secondary" className="gap-1">
                      {categories.find(c => c.id === selectedCategory)?.name}
                      <X
                        size={14}
                        className="cursor-pointer hover:text-red-600"
                        onClick={() => {
                          setSelectedCategory('');
                          const params = new URLSearchParams(searchParams);
                          params.delete('cat');
                          setSearchParams(params);
                        }}
                      />
                    </Badge>
                  )}
                  {selectedBrand && (
                    <Badge variant="secondary" className="gap-1">
                      {selectedBrand}
                      <X
                        size={14}
                        className="cursor-pointer hover:text-red-600"
                        onClick={() => {
                          setSelectedBrand('');
                          const params = new URLSearchParams(searchParams);
                          params.delete('marca');
                          setSearchParams(params);
                        }}
                      />
                    </Badge>
                  )}
                  {searchTerm && (
                    <Badge variant="secondary" className="gap-1">
                      "{searchTerm}"
                      <X
                        size={14}
                        className="cursor-pointer hover:text-red-600"
                        onClick={() => setSearchTerm('')}
                      />
                    </Badge>
                  )}
                  <Button variant="ghost" size="sm" onClick={clearFilters} className="text-red-600 hover:text-red-700">
                    Limpar tudo
                  </Button>
                </div>
              )}
            </div>

            {/* Results Count */}
            <div className="mb-6">
              <p className="text-gray-600">
                <span className="font-semibold text-gray-900">{filteredProducts.length}</span> produto(s) encontrado(s)
              </p>
            </div>

            {/* Products Grid */}
            {filteredProducts.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {filteredProducts.map((product) => {
                  // Encontrar logo da marca (case-insensitive)
                  const brandData = brands?.find(b => b.name.toLowerCase() === product.brand.toLowerCase());
                  const brandLogo = brandData?.logo;
                  
                  return (
                    <Card 
                      key={product.id} 
                      className="overflow-hidden hover:shadow-2xl transition-all hover:-translate-y-1 group cursor-pointer" 
                      data-testid={`product-card-${product.id}`}
                      onClick={() => setSelectedProduct(product)}
                    >
                      <div className="relative overflow-hidden bg-gray-100">
                        <img
                          src={product.images?.[0] || product.image || 'https://placehold.co/400x300?text=Produto'}
                          alt={product.name}
                          className="w-full h-48 md:h-56 object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        {/* Logo ou Badge da Marca */}
                        {brandLogo ? (
                          <div className="absolute top-3 left-3 bg-white/95 backdrop-blur-sm px-2 py-1.5 rounded-lg shadow-md">
                            <img src={brandLogo} alt={product.brand} className="h-5 w-auto max-w-[70px] object-contain" />
                          </div>
                        ) : (
                          <Badge className="absolute top-3 left-3 bg-blue-900 text-white">
                            {product.brand}
                          </Badge>
                        )}
                        {/* Indicador Ver Detalhes */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-center pb-4">
                          <span className="bg-white text-gray-900 px-5 py-2.5 rounded-full font-semibold shadow-lg flex items-center gap-2 text-sm">
                            <ZoomIn size={16} />
                            Ver detalhes
                          </span>
                        </div>
                      </div>
                      <CardContent className="p-4 md:p-6">
                        <h3 className="font-bold text-base md:text-lg text-gray-900 mb-2 line-clamp-2 group-hover:text-red-600 transition-colors">
                          {product.name}
                        </h3>
                        <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                          {product.description}
                        </p>
                        <div className="flex flex-col sm:flex-row gap-2">
                          <Button
                            className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                            onClick={(e) => { e.stopPropagation(); handleWhatsAppClick(product); }}
                            data-testid={`whatsapp-btn-${product.id}`}
                          >
                            <WhatsAppIcon className="w-4 h-4 mr-2" />
                            Consultar
                          </Button>
                          <Button
                            variant="outline"
                            className="flex-1 border-red-600 text-red-600 hover:bg-red-600 hover:text-white"
                            onClick={(e) => { e.stopPropagation(); window.location.href = '/orcamento?produto=' + product.id; }}
                            data-testid={`quote-btn-${product.id}`}
                          >
                            Orçamento
                          </Button>
                        </div>
                        {/* Botão Compartilhar */}
                        <Button
                          variant="ghost"
                          size="sm"
                          className={`w-full mt-2 ${copiedProductId === product.id ? 'text-green-600 bg-green-50' : 'text-gray-500 hover:text-blue-600 hover:bg-blue-50'}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            const slug = generateSlug(product);
                            const url = `${getSiteUrl()}/produto/${slug}`;
                            
                            // Copiar URL para clipboard
                            if (navigator.clipboard && navigator.clipboard.writeText) {
                              navigator.clipboard.writeText(url).then(() => {
                                setCopiedProductId(product.id);
                                setTimeout(() => setCopiedProductId(null), 2000);
                              }).catch(() => {
                                window.prompt('Copie o link:', url);
                              });
                            } else {
                              window.prompt('Copie o link:', url);
                            }
                          }}
                          data-testid={`share-btn-${product.id}`}
                        >
                          {copiedProductId === product.id ? (
                            <>
                              <Check size={14} className="mr-1" />
                              Link copiado!
                            </>
                          ) : (
                            <>
                              <Share2 size={14} className="mr-1" />
                              Compartilhar
                            </>
                          )}
                        </Button>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-16">
                <p className="text-xl text-gray-600 mb-4">Nenhum produto encontrado</p>
                <Button onClick={clearFilters} variant="outline">
                  Limpar filtros
                </Button>
              </div>
            )}
          </div>
        </div>

        <div className="lg:hidden mt-8">
          <BannerGrid position="catalog_side" columns={1} />
        </div>
      </div>

      {/* Modal de Produto */}
      <ProductModal 
        product={selectedProduct}
        isOpen={!!selectedProduct}
        onClose={() => setSelectedProduct(null)}
        onWhatsApp={handleWhatsAppClick}
        brands={brands}
        categories={categories}
      />
    </div>
  );
};
